import { cn } from "@/lib/utils";

interface AppBrandProps {
  name?: string | null;
  logoUrl?: string | null;
  collapsed?: boolean;
  subtitle?: string;
  variant?: "sidebar" | "header";
}

const fallbackName = "RoofGenius";

const formatBrandName = (value?: string | null) => {
  const raw = value?.trim() || fallbackName;
  const withoutAiSuffix = raw.replace(/\s+AI$/i, "");

  if (withoutAiSuffix.toUpperCase() !== withoutAiSuffix) return withoutAiSuffix;

  return withoutAiSuffix
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .replace(/Roofgenius/g, "RoofGenius");
};

const getBrandInitials = (value: string) =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("") || "RG";

export function AppBrand({ name, logoUrl, collapsed = false, subtitle, variant = "header" }: AppBrandProps) {
  const displayName = formatBrandName(name);
  const isSidebar = variant === "sidebar";

  return (
    <div className={cn("flex min-w-0 items-center", collapsed ? "justify-center" : "gap-3")}>
      <div
        className={cn(
          "brand-mark flex shrink-0 items-center justify-center overflow-hidden border border-border bg-card text-primary",
          isSidebar && "border-sidebar-border bg-sidebar-accent text-sidebar-primary dark:border-sidebar-primary/40 dark:bg-sidebar-accent",
          isSidebar ? "rounded-lg" : "rounded-md",
          collapsed ? "h-9 w-9" : isSidebar ? "h-11 w-11" : "h-9 w-9",
        )}
      >
        {logoUrl ? (
          <img src={logoUrl} alt={`${displayName} logo`} className="h-full w-full object-contain p-1" />
        ) : (
          <span className={cn("font-bold leading-none", collapsed ? "text-xs" : "text-sm")}>
            {getBrandInitials(displayName)}
          </span>
        )}
      </div>

      {!collapsed && (
        <div className="min-w-0">
          <div className={cn(
            "truncate font-semibold normal-case",
            isSidebar ? "brand-name text-base text-sidebar-foreground" : "text-sm text-foreground md:text-base"
          )}>
            {displayName}
          </div>
          {subtitle && (
            <div className={cn(
              "truncate text-[9px] font-semibold uppercase tracking-[0.14em]",
              isSidebar ? "text-sidebar-foreground/70" : "text-muted-foreground"
            )}>
              {subtitle}
            </div>
          )}
        </div>
      )}
    </div>
  );
}